import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createCustomer, deleteCustomer, fetchCustomers, updateCustomer } from '../../api/customersModule';
import RoleGuard from '../../components/RoleGuard';
import { useToast } from '../../components/ToastContext';

const statusOptions = ['ACTIVE', 'INACTIVE'];

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-lg">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600"
        >
          Close
        </button>
      </div>
      <div className="pt-4">{children}</div>
    </div>
  </div>
);

const CustomerForm = ({ initialValue, onSubmit, submitting }) => {
  const [form, setForm] = useState(
    initialValue || { firstName: '', lastName: '', email: '', phone: '', status: 'ACTIVE' }
  );

  const onChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">
          First name
          <input
            value={form.firstName}
            onChange={onChange('firstName')}
            required
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Last name
          <input
            value={form.lastName}
            onChange={onChange('lastName')}
            required
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </label>
      </div>
      <label className="text-sm font-medium text-slate-700">
        Email
        <input
          value={form.email}
          onChange={onChange('email')}
          type="email"
          required
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
        />
      </label>
      <label className="text-sm font-medium text-slate-700">
        Phone
        <input
          value={form.phone || ''}
          onChange={onChange('phone')}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
        />
      </label>
      <label className="text-sm font-medium text-slate-700">
        Status
        <select
          value={form.status || 'ACTIVE'}
          onChange={onChange('status')}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
      >
        {submitting ? 'Saving...' : 'Save'}
      </button>
    </form>
  );
};

const CustomersList = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const basePath = useMemo(() => (location.pathname.startsWith('/admin') ? '/admin' : ''), [
    location.pathname,
  ]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', filters, page],
    queryFn: () => fetchCustomers({ ...filters, page, limit: 10 }),
    keepPreviousData: true,
  });

  const customers = data?.data || [];
  const meta = data?.meta || {};

  useEffect(() => {
    setPage(1);
  }, [filters.search, filters.status]);

  const createMutation = useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      toast.success('Customer created');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setModalOpen(false);
    },
    onError: (err) => toast.error(err?.message || 'Create failed'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateCustomer(id, payload),
    onSuccess: () => {
      toast.success('Customer updated');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setModalOpen(false);
      setEditing(null);
    },
    onError: (err) => toast.error(err?.message || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      toast.success('Customer removed');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err) => toast.error(err?.message || 'Delete failed'),
  });

  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Customers</p>
          <h1 className="text-xl font-bold text-slate-900">Customers list</h1>
          <p className="text-sm text-slate-600">Manage customer profiles and statuses.</p>
        </div>
        <RoleGuard>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Add customer
          </button>
        </RoleGuard>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={filters.search}
          onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
          placeholder="Search name, email, phone"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none md:max-w-sm"
        />
        <select
          value={filters.status}
          onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
        >
          <option value="">All statuses</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error.message || 'Failed to load customers'}
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && customers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  No customers found.
                </td>
              </tr>
            )}
            {customers.map((customer) => (
              <tr key={customer.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">
                  {customer.firstName} {customer.lastName}
                </td>
                <td className="px-4 py-3 text-slate-700">{customer.email}</td>
                <td className="px-4 py-3 text-slate-700">{customer.phone || '-'}</td>
                <td className="px-4 py-3 text-xs">
                  <span className="rounded-full border border-slate-200 px-2 py-1 font-semibold text-slate-600">
                    {customer.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`${basePath}/customers/${customer.id}`)}
                      className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      View
                    </button>
                    <RoleGuard>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(customer);
                          setModalOpen(true);
                        }}
                        className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Delete this customer?')) {
                            deleteMutation.mutate(customer.id);
                          }
                        }}
                        className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </RoleGuard>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {meta.page || 1} of {Math.max(1, meta.totalPages || 1)}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={(meta.page || 1) <= 1}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={(meta.page || 1) >= (meta.totalPages || 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Edit customer' : 'Add customer'}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
        >
          <CustomerForm
            initialValue={editing}
            submitting={createMutation.isPending || updateMutation.isPending}
            onSubmit={(payload) => {
              if (editing) {
                updateMutation.mutate({ id: editing.id, payload });
              } else {
                createMutation.mutate(payload);
              }
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default CustomersList;
